import React, { FC, MouseEventHandler, ReactNode } from 'react';

import styled from '@emotion/styled';
import { compose, TypographyProps, typography, SpaceProps, space, variant } from '@lucid/theme';
import { AccordionItemButton } from 'react-accessible-accordion';

import { Component } from '../../types/types';
import Box from '../box/box';
import Icon from '../icon/icon';

export interface AccordionButtonProps extends TypographyProps, SpaceProps {
  /** Class(es) to apply to element. */
  className?: string;
  /** The children to be rendered with the Accordion.Button. */
  children?: ReactNode;
  /** Renders an arrow for the `Accordion.Item`. */
  hasArrow?: boolean;
  /** Changes the color for the icon. */
  iconColor?: string;
  /** Changes the onHover color for the icon. */
  iconHoverColor?: string;
  /** Passes a custom onClick handler to the `Accordion.Button` component. ` */
  onClick?: () => (void | Promise<boolean>) | MouseEventHandler<HTMLButtonElement>;
  /** The available variants for the `Accordion.Button` component. */
  variant?: Component.Type['Accordion']['Button'];
}

const accordionButtonStyleProp = compose(typography, space);

const StyledAccordionButton = styled(AccordionItemButton)(
  variant({
    scale: 'accordion.button',
    variants: {
      defaultVariant: {},
    },
  }),
  {
    label: 'button',
  },
  accordionButtonStyleProp,
);

const AccordionButton: FC<AccordionButtonProps> = ({
  children,
  className,
  hasArrow,
  iconColor,
  iconHoverColor,
  onClick,
  variant = 'default',
}) => (
  <StyledAccordionButton
    className={className}
    css={{
      '& > .accordionButtonHasArrow': {
        transition: 'transform 0.15s ease-out',
      },
    }}
    variant={variant}
  >
    <Box
      onClick={() => {
        if (onClick) {
          onClick();
        }
      }}
    >
      {children}
    </Box>
    {hasArrow && (
      <Icon className="accordionButtonHasArrow" color={iconColor} hoverColor={iconHoverColor} type="CarotRight" />
    )}
  </StyledAccordionButton>
);

export { AccordionButton };
export default AccordionButton;
